import { OptionTemplate } from "shared/api/middleware-api-client";
import { BeamData } from "./DoorOptionsProvider.types";
import {
  defaultRoundedExitRestoreCodes,
  doorHandleProfiles,
  findFirstHandleByCodes,
  findFirstMatchingHandle,
  findFirstNonUHandle,
  genericInvalidHandleCodes,
  hiddenHandleCodesForClassicLayouts,
  hiddenHandleCodesForNewLayouts,
  isConditionalUHandleAllowed,
  isLockHandleAllowedOnLayout,
  isUHandleCode,
  isVerticalOnlyDoorCode,
  maxUHandleBeamGap,
  roundedHandleCodes,
  roundedLayoutExitHandleCodes,
  shouldKeepConditionalDoubleHandleVisible,
  type DoorHandleProfile,
  type HandleContext,
  type HandleFamilyPolicy,
  type HandleModeConfig,
  type RestrictedHandleFamilyPolicy,
  uHandleCodes,
} from "./doorHandleProfiles";
import {
  isConditionalUNewLayoutCode,
  isNewLayoutCode,
  isRoundedHandleLayoutCode,
} from "entities/configurator/model/layoutMeta";

export type { HandleFamilyPolicy } from "./doorHandleProfiles";
export { genericInvalidHandleCodes, maxUHandleBeamGap, uHandleCodes };

export type HandlePresentation = "hidden" | "disabled" | "enabled";

export type HandleRepairReason = "beam-gap-invalid" | "policy-invalid";

export type HandleRepairResolution = {
  nextHandle: OptionTemplate;
  reason: HandleRepairReason;
};

const getDoorHandleProfile = (
  doorCode: string | null | undefined,
): DoorHandleProfile | null => {
  if (!doorCode) {
    return null;
  }

  return doorHandleProfiles[doorCode] ?? null;
};

const getHandleModeConfig = (
  doorCode: string | null | undefined,
  policy: RestrictedHandleFamilyPolicy,
): HandleModeConfig | null => getDoorHandleProfile(doorCode)?.[policy] ?? null;

const getRoundedExitRestoreCodes = (
  doorCode: string | null | undefined,
): readonly string[] =>
  getDoorHandleProfile(doorCode)?.roundedExitRestoreCodes ??
  defaultRoundedExitRestoreCodes;

const isHiddenOnLayout = (
  handleCode: string | null | undefined,
  doorCode: string | null | undefined,
  layoutCode: string | null | undefined,
): boolean => {
  if (!handleCode) {
    return false;
  }

  if (isNewLayoutCode(layoutCode)) {
    return (
      hiddenHandleCodesForNewLayouts.includes(handleCode) &&
      !shouldKeepConditionalDoubleHandleVisible(
        handleCode,
        doorCode,
        layoutCode,
      ) &&
      !isLockHandleAllowedOnLayout(handleCode, layoutCode)
    );
  }

  if (!layoutCode) {
    return false;
  }

  return hiddenHandleCodesForClassicLayouts.includes(handleCode);
};

export const getHandleAnchorBeamGap = (
  beams: BeamData[] | null | undefined,
): number | null => {
  const anchorBeams = (beams ?? []).filter((beam) => beam.handleAnchor);

  if (anchorBeams.length < 2) {
    return null;
  }

  return Math.abs(anchorBeams[0].height - anchorBeams[1].height);
};

export const isUsableHandleOption = (
  handle: OptionTemplate | null | undefined,
  layoutCode?: string | null,
): handle is OptionTemplate =>
  !!handle &&
  handle.type === "handle" &&
  !handle.disabled &&
  !!handle.code &&
  (!genericInvalidHandleCodes.includes(handle.code) ||
    isLockHandleAllowedOnLayout(handle.code, layoutCode));

const getUsableHandles = (
  handleOptions: OptionTemplate[] | undefined,
  layoutCode?: string | null,
): OptionTemplate[] =>
  (handleOptions ?? []).filter((handle) =>
    isUsableHandleOption(handle, layoutCode),
  );

export const getHandleFamilyPolicy = (
  doorCode: string | null | undefined,
  layoutCode: string | null | undefined,
): HandleFamilyPolicy => {
  if (
    isRoundedHandleLayoutCode(layoutCode) &&
    getHandleModeConfig(doorCode, "rounded-layout")
  ) {
    return "rounded-layout";
  }

  const conditionalLayoutMatcher =
    getDoorHandleProfile(doorCode)?.conditionalULayoutMatcher;
  if (conditionalLayoutMatcher?.(layoutCode)) {
    return "conditional-u-new-layout";
  }

  if (
    isVerticalOnlyDoorCode(doorCode) &&
    isConditionalUNewLayoutCode(layoutCode)
  ) {
    return "vertical-only";
  }

  return "unrestricted";
};

const defaultIsAllowedByPolicy: Record<
  RestrictedHandleFamilyPolicy,
  (handleCode: string | null | undefined, ctx: HandleContext) => boolean
> = {
  "rounded-layout": (handleCode) => !!handleCode && !isUHandleCode(handleCode),
  "conditional-u-new-layout": (handleCode, { anchorBeamGap, layoutCode }) =>
    !!handleCode &&
    (!isUHandleCode(handleCode) ||
      isConditionalUHandleAllowed(anchorBeamGap, layoutCode)),
  "vertical-only": (handleCode) => isUHandleCode(handleCode),
};

export const isHandleAllowedForPolicy = (
  handleCode: string | null | undefined,
  policy: HandleFamilyPolicy,
  doorCode: string | null | undefined,
  anchorBeamGap?: number | null,
  layoutCode?: string | null,
): boolean => {
  if (policy === "unrestricted") {
    return true;
  }

  const isAllowed =
    getHandleModeConfig(doorCode, policy)?.isAllowed ??
    defaultIsAllowedByPolicy[policy];

  return isAllowed(handleCode, { anchorBeamGap, layoutCode });
};

export const getHandlePresentation = ({
  anchorBeamGap,
  doorCode,
  handleCode,
  layoutCode,
}: {
  anchorBeamGap?: number | null;
  doorCode: string | null | undefined;
  handleCode: string | null | undefined;
  layoutCode: string | null | undefined;
}): HandlePresentation => {
  if (isHiddenOnLayout(handleCode, doorCode, layoutCode)) {
    return "hidden";
  }

  const policy = getHandleFamilyPolicy(doorCode, layoutCode);
  return isHandleAllowedForPolicy(
    handleCode,
    policy,
    doorCode,
    anchorBeamGap,
    layoutCode,
  )
    ? "enabled"
    : "disabled";
};

const getPreferredHandleCodesForPolicy = (
  doorCode: string | null | undefined,
  layoutCode: string | null | undefined,
): readonly string[] => {
  const policy = getHandleFamilyPolicy(doorCode, layoutCode);
  if (policy === "unrestricted") {
    return [];
  }

  const modeConfig = getHandleModeConfig(doorCode, policy);
  if (modeConfig?.preferredCodes) {
    return modeConfig.preferredCodes;
  }

  return policy === "vertical-only" ? uHandleCodes : [];
};

const findFallbackHandleForPolicy = ({
  anchorBeamGap,
  doorCode,
  handleOptions,
  layoutCode,
  policy,
}: {
  anchorBeamGap?: number | null;
  doorCode: string | null | undefined;
  handleOptions: OptionTemplate[] | undefined;
  layoutCode: string | null | undefined;
  policy: RestrictedHandleFamilyPolicy;
}): OptionTemplate | null => {
  const usableHandles = getUsableHandles(handleOptions, layoutCode);
  const modeConfig = getHandleModeConfig(doorCode, policy);
  if (modeConfig?.findFallback) {
    return modeConfig.findFallback(usableHandles, {
      anchorBeamGap,
      layoutCode,
    });
  }

  if (policy === "rounded-layout" || policy === "conditional-u-new-layout") {
    return findFirstNonUHandle(usableHandles);
  }

  return (
    usableHandles.find((handle) =>
      isHandleAllowedForPolicy(
        handle.code,
        policy,
        doorCode,
        anchorBeamGap,
        layoutCode,
      ),
    ) ?? null
  );
};

const shouldKeepHandleOnTransition = (
  doorCode: string | null | undefined,
  nextPolicy: RestrictedHandleFamilyPolicy,
  handleCode: string,
  nextLayoutCode: string | null | undefined,
): boolean => {
  if (nextPolicy === "rounded-layout") {
    return roundedHandleCodes.includes(handleCode);
  }

  if (nextPolicy === "vertical-only") {
    return isHandleAllowedForPolicy(
      handleCode,
      nextPolicy,
      doorCode,
      undefined,
      nextLayoutCode,
    );
  }

  return (
    getHandleModeConfig(
      doorCode,
      "conditional-u-new-layout",
    )?.shouldKeepOnTransition?.(handleCode, { layoutCode: nextLayoutCode }) ??
    false
  );
};

export const resolvePreferredHandleForPolicy = ({
  anchorBeamGap,
  doorCode,
  layoutCode,
  handleOptions,
}: {
  anchorBeamGap?: number | null;
  doorCode: string | null | undefined;
  layoutCode: string | null | undefined;
  handleOptions: OptionTemplate[] | undefined;
}): OptionTemplate | null => {
  const policy = getHandleFamilyPolicy(doorCode, layoutCode);
  if (policy === "unrestricted") {
    return null;
  }

  const preferredHandle = findFirstHandleByCodes(
    getUsableHandles(handleOptions, layoutCode),
    getPreferredHandleCodesForPolicy(doorCode, layoutCode),
  );
  if (preferredHandle) {
    return preferredHandle;
  }

  return findFallbackHandleForPolicy({
    anchorBeamGap,
    doorCode,
    handleOptions,
    layoutCode,
    policy,
  });
};

export const resolveActiveHandleForPolicy = ({
  anchorBeamGap,
  currentHandle,
  doorCode,
  handleOptions,
  layoutCode,
}: {
  anchorBeamGap?: number | null;
  doorCode: string | null | undefined;
  layoutCode: string | null | undefined;
  currentHandle: OptionTemplate | null | undefined;
  handleOptions: OptionTemplate[] | undefined;
}): HandleRepairResolution | null => {
  const policy = getHandleFamilyPolicy(doorCode, layoutCode);
  if (policy === "unrestricted") {
    return null;
  }

  const selectedHandle = getUsableHandles(handleOptions, layoutCode).find(
    (handle) => handle.code === currentHandle?.code,
  );
  if (
    selectedHandle &&
    isHandleAllowedForPolicy(
      selectedHandle.code,
      policy,
      doorCode,
      anchorBeamGap,
      layoutCode,
    )
  ) {
    return null;
  }

  const nextHandle = resolvePreferredHandleForPolicy({
    anchorBeamGap,
    doorCode,
    layoutCode,
    handleOptions,
  });
  if (!nextHandle) {
    return null;
  }

  const isBeamGapInvalidRepair =
    policy === "conditional-u-new-layout" &&
    isUHandleCode(currentHandle?.code) &&
    anchorBeamGap !== null &&
    anchorBeamGap !== undefined &&
    !isConditionalUHandleAllowed(anchorBeamGap, layoutCode);

  return {
    nextHandle,
    reason: isBeamGapInvalidRepair ? "beam-gap-invalid" : "policy-invalid",
  };
};

export const resolveHandleForLayoutTransition = ({
  currentHandle,
  currentLayoutCode,
  doorCode,
  handleOptions,
  nextLayoutCode,
}: {
  doorCode: string | null | undefined;
  currentLayoutCode: string | null | undefined;
  nextLayoutCode: string | null | undefined;
  currentHandle: OptionTemplate | null | undefined;
  handleOptions: OptionTemplate[] | undefined;
}): OptionTemplate | null => {
  const nextPolicy = getHandleFamilyPolicy(doorCode, nextLayoutCode);
  const currentPolicy = getHandleFamilyPolicy(doorCode, currentLayoutCode);

  if (nextPolicy !== "unrestricted") {
    if (
      isUsableHandleOption(currentHandle, nextLayoutCode) &&
      shouldKeepHandleOnTransition(
        doorCode,
        nextPolicy,
        currentHandle.code,
        nextLayoutCode,
      )
    ) {
      return null;
    }

    return resolvePreferredHandleForPolicy({
      doorCode,
      layoutCode: nextLayoutCode,
      handleOptions,
    });
  }

  if (
    (currentPolicy === "rounded-layout" ||
      currentPolicy === "conditional-u-new-layout") &&
    isUsableHandleOption(currentHandle) &&
    roundedLayoutExitHandleCodes.includes(currentHandle.code)
  ) {
    return findFirstHandleByCodes(
      getUsableHandles(handleOptions),
      getRoundedExitRestoreCodes(doorCode),
    );
  }

  return null;
};
